import React from 'react';
import {PermissionTypeKr} from '@di-shared/api/di/type';
import ImageSelector from '@di-shared/features/common/ImageSelector';
import {t, useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import BatteryDischargeEstimator from '@di-shared/features/diag/battery/discharge/BatteryDischargeEstimator';
import {BatteryStatusGood, BatteryStatusWarning} from '@di-shared/features/diag/battery/discharge/BatteryStatusIndicator';
import DrivingDurationDifference from '@di-shared/features/diag/battery/discharge/DrivingDurationDifference';
import ElectronicDeviceDetection from '@di-shared/features/diag/battery/discharge/ElectronicDeviceDetection';
import RemainingBatteryLife from '@di-shared/features/diag/battery/discharge/RemainingBatteryLife';
import useBatteryStatus from '@di-shared/features/diag/battery/useBatteryStatus';
import useAvailableComponentKr from '@di-shared/features/home/kr/useAvailableComponent.kr';

interface BatteryDischargeRiskProps {
  vin: string;
}

function BatteryDischargeRisk({vin}: BatteryDischargeRiskProps) {
  const {dischargeRisk, externalDevice, statusDetected} = useBatteryStatus(vin);
  const {checkAvailableComponent} = useAvailableComponentKr({vin});

  if (!statusDetected) {
    return <BatteryStatusNotFound />;
  }

  return (
    <div
      className="tab_content discharge_risk"
      id={`${t('T30075')}_tabpanel`}
      aria-labelledby={`${t('T30075')}_tab`}
    >
      <section className="section">
        {dischargeRisk ? <BatteryStatusWarning /> : <BatteryStatusGood />}
        {/**
         * [2026-09-09:Jeyoung Lee]
         * @link https://hmg.atlassian.net/browse/DRIVINGINS-5281
         * FE에서 주행기록의 EV에너지사용정보 기준으로 EV차량임을 판단하는 것으로 변경
         * 추후 EREV 차량에 대해서는 재확인 필요
         */}
        {!checkAvailableComponent(PermissionTypeKr.EV_ENERGY_USAGE) && <DrivingDurationDifference vin={vin} />}
        <RemainingBatteryLife vin={vin} />
        {externalDevice?.detected && (
          <>
            <BatteryDischargeEstimator vin={vin} />
            <ElectronicDeviceDetection vin={vin} />
            <div className="driving_banner type_error">
              <div className="driving_banner_box">
                <span className="ic ic_caution_18px"></span>
                <span className="driving_banner_txtbox">
                  <span className="driving_banner_desc body_small_r">{t('T30362')}</span>
                </span>
              </div>
            </div>
          </>
        )}
      </section>
    </div>
  );
}

export default BatteryDischargeRisk;

export function BatteryDischargeRiskLoading() {
  return (
    <div className="tab_content discharge_risk">
      <section className="section">
        <div className="section_heading type_main">
          <h2
            className="section_heading_title skeleton"
            style={{height: 60}}
          />
          <p
            className="section_heading_desc skeleton"
            style={{height: 54}}
          />
        </div>
        <div className="section_visual visual_battery skeleton">
          <div
            className="section_visual_img"
            style={{height: 142.5}}
          />
        </div>
        <div
          className="section_container skeleton"
          style={{height: 191}}
        />
        <div
          className="section_container skeleton"
          style={{height: 202}}
        />
        <div
          className="section_container skeleton"
          style={{height: 227}}
        />
        <div
          className="section_container skeleton"
          style={{height: 313.98}}
        />
      </section>
    </div>
  );
}

export function BatteryStatusNotFound() {
  const {t} = useInferableTranslation();

  return (
    <div className="tab_content discharge_risk">
      <section className="section">
        <div className="section_heading type_main">
          <h2
            className="section_heading_title"
            style={{whiteSpace: 'pre-wrap'}}
          >
            {t('T30095')}
          </h2>
          <p className="section_heading_desc">
            {t('T30096')}
            <span
              className="section_heading_sub_desc"
              style={{whiteSpace: 'pre-wrap'}}
            >
              {t('T30363')}
            </span>
          </p>
        </div>
        <div className="section_visual visual_battery">
          <ImageSelector
            className="section_visual_img"
            alt="section_visual_img"
            kiaLightSrc={() => import('@di-shared/assets/img/visual/battery_nodata_light.png')}
            kiaDarkSrc={() => import('@di-shared/assets/img/visual/battery_nodata.png')}
            hyundaiLightSrc={() => import('@di-shared/assets/img/visual/battery_nodata_light.png')}
            hyundaiDarkSrc={() => import('@di-shared/assets/img/visual/battery_nodata.png')}
            genesisLightSrc={() => import('@di-shared/assets/img/visual/battery_nodata_light.png')}
            genesisDarkSrc={() => import('@di-shared/assets/img/visual/battery_nodata.png')}
          />
        </div>
      </section>
    </div>
  );
}
